import { Component, EventEmitter, Input, OnInit, Output, AfterViewInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, finalize, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { Category } from '../../../models/domain/Category';
import { CategoryService } from '../../../services/category.service';
import { LoadingService } from '../../../services/loading.service';
import { ObjectUtility } from '../../../utilities/ObjectUtility';
import { whitespaceValidator } from '../../../validators/ValidationService';
import { Tenant } from '../../../models/domain/Tenant';
import { TenantService } from '../../../services/tenant.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { CapitalizeWordsDirective } from '../../../directive/CapitalizeWordsDirective';

@Component({
  selector: 'app-category-details',
  templateUrl: './category-details.component.html',
  styleUrl: './category-details.component.scss',
  imports: [ReactiveFormsModule, CommonModule, CapitalizeWordsDirective],
})

export class CategoryDetailsComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output() classApplied = new EventEmitter<boolean>();
  @Output() saveConfirmationEvent = new EventEmitter<boolean>();
  @Input() currentCategoryId = 0;

  // isToggled
  isToggled = false;
  frmGroup!: FormGroup;

  private destroy$: Subject<boolean> = new Subject<boolean>();
  public categoryOriginal = new Category;
  public category: Category = new Category;
  public titleText = "";
  public titleButton = "";
  public errorMessage = "";
  isSubmitted = false;

  public tenant$!: Observable<Tenant[]>;
  public tenant!: Tenant[];

  constructor(
    public themeService: CustomizerSettingsService,
    private formBuilder: FormBuilder,
    private categoryService: CategoryService,
    private toasterService: ToastrService,
    private loadingService: LoadingService,
    private tenantService: TenantService,
    private authService: AuthenticationService,

  ) {
    this.initialize();
  }

  public ngOnInit(): void {
    if (this.currentCategoryId > 0) {
      this.getDropdownData();
    }
    else {
      this.currentCategoryId = 0;
      this.getDropdownData();
      const newCategory = new Category();
      this.category = ObjectUtility.deepClone(newCategory);
      this.categoryOriginal = ObjectUtility.deepClone(newCategory);
      this.setForm(this.category);
    }
  }

  private initialize(): void {
    this.themeService.isToggled$.subscribe(isToggled => {
      this.isToggled = isToggled;
    });
    this.isSubmitted = false;
    this.createForm();
  }

  public ngAfterViewInit(): void {

    this.frmGroup.controls['nameControl'].valueChanges.subscribe(value => {
      this.category.name = value;
    });

    this.frmGroup.controls['tenantControl'].valueChanges.subscribe(value => {
      this.category.tenant = value;
    });

  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  get f() { return this.frmGroup.controls; }

  private createForm(): void {
    this.frmGroup = this.formBuilder.group({
      nameControl: new FormControl('', [Validators.required, whitespaceValidator()]),
      tenantControl: new FormControl(null, [Validators.required]),
    })
  }

  private setForm(category: Category): void {
    if (category) {
      this.frmGroup.patchValue({
        nameControl: category.name,
        tenantControl: category.tenant,
      });
    }
    this.titleText = `${this.currentCategoryId === 0 ? "Add" : "Edit"} Category`;
    this.titleButton = `${this.currentCategoryId === 0 ? "Create" : "Update"}`;
  }

  public onSave(): void {
    this.isSubmitted = true;
    if (this.frmGroup.valid) {
      this.loadingService.showLoading();
      this.addEditCategory();
    }
  }

  private addEditCategory(): void {
    if (this.category?.id === 0) {
      this.categoryService.create(this.sanitizeService(this.category))
        .pipe(
          catchError((err: any) => {
            if (err && err.error) {
              this.isSubmitted = false;
              if (err.error.isCustom) {
                this.errorMessage = err.error.message;
                this.toasterService.warning(err.error.message);
              } else {
                this.toasterService.error('Something went wrong!');
              }
            } else {
              this.isSubmitted = false;
              this.toasterService.error('Something went wrong!');
            }
            this.loadingService.hideLoading();

            return of(null);
          }),
          finalize(() => {
            this.loadingService.hideLoading();
          }),
          takeUntil(this.destroy$))
        .subscribe(response => {
          if (response) {
            this.isSubmitted = false;
            this.category = response?.body != null ? response?.body : new Category;
            this.saveConfirmationEvent.emit(true);
            this.toggleClass();
            this.toasterService.success("Save successfully");
          }
          this.loadingService.hideLoading();
        });
    } else {
      this.categoryService.update(this.sanitizeService(this.category))
        .pipe(
          catchError((err: any) => {
            if (err && err.error) {
              this.isSubmitted = false;
              if (err.error.isCustom) {
                this.errorMessage = err.error.message;
                this.toasterService.warning(err.error.message);
              } else {
                this.toasterService.error('Something went wrong!');
              }
            } else {
              this.isSubmitted = false;
              this.toasterService.error('Something went wrong!');
            }
            return of(null);
          }),
          takeUntil(this.destroy$),
          // Ensure loading is always hidden, even if error is not caught
          finalize(() => {
            this.loadingService.hideLoading();
          })
        )
        .subscribe(response => {
          if (response) {
            this.isSubmitted = false;
            this.category = response?.body != null ? response?.body : new Category;
            this.saveConfirmationEvent.emit(true);
            this.toasterService.success("Update successfully");
            this.toggleClass();
          }
        });
    }
  }

  private sanitizeService(category: Category): Category {
    category.tenant = null;
    category.tenantId = this.frmGroup.controls['tenantControl'].value.id;

    return category;
  }

  public toggleClass(): void {
    this.classApplied.emit(false);
  }

  public dataHasChanged(): boolean {
    const baseEqual = ObjectUtility.deepEqual(this.category, this.categoryOriginal);
    return !baseEqual;
  }

  public formIsValid(): boolean {
    const isValid = this.frmGroup.controls['nameControl'].valid &&
      this.frmGroup.controls['tenantControl'].valid
    return isValid;
  }


  private getDropdownData() {
    this.loadingService.showLoading();

    this.tenant$ = this.tenantService.get().pipe(
      map((result: any) => {
        this.loadingService.hideLoading();
        //return result.body.values
        const tenants = result.body.values;

        // Automatically select the logged-in user's tenantId
        if (this.currentCategoryId === 0) {
          const currentUser = this.authService.getCurrentUser();
          if (currentUser) {
            const loggedInTenantId = currentUser.tenantId;

            // Check if the logged-in user's tenantId exists in the tenant list
            const defaultTenant = tenants.find((tenant: any) => tenant.id === loggedInTenantId);
            if (defaultTenant) {
              this.frmGroup.controls['tenantControl'].setValue(defaultTenant);
            }
          }
        }
        return tenants;
      }),
      catchError(() => {
        this.loadingService.hideLoading();
        this.toasterService.error('Tenant data could not be loaded.');

        return of([])
      })
    );

    this.getCategoryById(this.currentCategoryId);
  }

  public getCategoryById(dieId: number) {
    if (dieId) {
      this.loadingService.showLoading();
      this.categoryService.getById(dieId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(result => {
          if (result) {
            this.category = ObjectUtility.deepClone(result);
            this.categoryOriginal = ObjectUtility.deepClone(result);
            this.setForm(this.category);
          }
          this.loadingService.hideLoading();
        });
    }
  }

  //#region Dropdown compare

  public tenantComparer(t1: Tenant, t2: Tenant): boolean {
    return t1 && t2 ? (t1.id === t2.id || t1.name === t2.name) : t1 === t2;
  }

  // #endregion

}